import { useState, useEffect, useCallback, useRef } from "react";
import { useWeb3 } from "../context/Web3Context";
import { getEthersProvider } from "../web3/provider";
import { getTokenContract, getDexContract, getUsdtContract } from "../web3/contracts";
import { POLL_INTERVAL_MS } from "../config/app.config";

// Polls token balance, USDT balance, token->DEX allowance, and price for the
// currently selected market. Interval-based (no event callbacks).
export function useSwapData(market) {
  const { account, isConnected, isCorrectNetwork } = useWeb3();
  const [data, setData] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    if (!account || !isCorrectNetwork || !market) { setData(null); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const provider = getEthersProvider();
      const token = getTokenContract(market.token, provider);
      const dex = getDexContract(market.dex, provider);
      const usdt = getUsdtContract(provider);

      const [tokenBal, usdtBal, allowance, price] = await Promise.all([
        token.balanceOf(account),
        usdt.balanceOf(account),
        token.allowance(account, market.dex),
        dex[market.priceMethod]().catch(() => null),
      ]);

      if (!mounted.current) return;
      setData({ tokenBal, usdtBal, allowance, price });
    } catch {
      /* keep last data */
    } finally {
      inFlight.current = false;
    }
  }, [account, isCorrectNetwork, market]);

  useEffect(() => {
    mounted.current = true;
    if (!isConnected || !isCorrectNetwork) { setData(null); return; }
    setData(null);          // clear when market/account changes
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => { mounted.current = false; clearInterval(id); };
  }, [isConnected, isCorrectNetwork, load]);

  return { data, refresh: load };
}
